import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { roleService } from '../services/api';
import {
  PERMISSION_MATRIX,
  getAllPermissionKeys,
} from '../utils/permissionMatrix';

function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', permissions: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAll();
      setRoles(response.data.results || response.data);
    } catch (err) {
      console.error('Помилка завантаження ролей:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildInitialPermissions = (rolePermissions) => {
    const raw = rolePermissions && typeof rolePermissions === 'object' ? rolePermissions : {};
    const keys = getAllPermissionKeys();
    const result = {};
    keys.forEach((key) => {
      result[key] = raw[key] === true;
    });
    return result;
  };

  const handleOpen = (role = null) => {
    setError('');
    if (role) {
      setEditing(role.id);
      setFormData({
        name: role.name,
        permissions: buildInitialPermissions(role.permissions),
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        permissions: buildInitialPermissions({}),
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setError('');
  };

  const setPermission = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value },
    }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.name.trim()) {
      setError('Назва ролі обовʼязкова');
      return;
    }
    // true = дозволено, false = заборонено (усі ключі матриці явно)
    const permissions = getAllPermissionKeys().reduce((acc, key) => {
      acc[key] = formData.permissions[key] === true;
      return acc;
    }, {});

    setSaving(true);
    try {
      if (editing) {
        await roleService.update(editing, { name: formData.name.trim(), permissions });
      } else {
        await roleService.create({ name: formData.name.trim(), permissions });
      }
      await loadData();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.name?.[0] || err.response?.data?.detail || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити цю роль?')) return;
    try {
      await roleService.delete(id);
      await loadData();
    } catch (err) {
      console.error('Помилка видалення:', err);
      alert(err.response?.data?.detail || 'Не вдалося видалити');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Ролі</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати роль
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Права (увімкнені модулі)</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="textSecondary">Немає ролей</Typography>
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>
                    {role.permissions && typeof role.permissions === 'object'
                      ? Object.entries(role.permissions)
                          .filter(([, v]) => v === true)
                          .map(([k]) => {
                            const section = PERMISSION_MATRIX.find((s) =>
                              s.permissions.some((p) => p.key === k)
                            );
                            const perm = section?.permissions.find((p) => p.key === k);
                            return perm?.label || k;
                          })
                          .join(', ') || '—'
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(role)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(role.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати роль' : 'Нова роль'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Typography color="error">{error}</Typography>
            )}
            <TextField
              label="Назва ролі"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Права доступу (матриця). Галочка = модуль у меню і доступ за посиланням.
            </Typography>
            {PERMISSION_MATRIX.map(({ section, permissions: perms }) => (
              <Box key={section} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {section}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {perms.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={formData.permissions[key] === true}
                          onChange={(e) => setPermission(key, e.target.checked)}
                        />
                      }
                      label={label}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RolesPage;
